import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { useIntl } from "react-intl";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { ConfirmationModal, PageActionProps } from "@/components";
import { Dialog } from "@/components/ui";
import { Loader2 } from "lucide-react";
import {
  ClientNotes,
  Media,
  UploadProgressMap,
  VideoPreferences,
  VideoProject,
  VideoProjectDetail,
  VideoProjectMessage,
  VideoProjectStatus,
  VideoProjectUpdateRequest,
  CoachSettings,
} from "@/interfaces";

import {
  URLS,
  capitalizeFormatter,
  validatePendingAck,
  formatHexColor,
  usePlayerSettings,
  useComplianceChecker,
  useSocialNetworks,
} from "@/utils";
import { CoachService, VideoProjectServices } from "@/services";

import { EditorProjectFiles } from "./components/EditorProjectFiles";
import { EditorPostInfo } from "./components/EditorPostInfo";
import { CaptionCoachDialog } from "@/components/CaptionCoach/CaptionCoachDialog";
import { DuplicateVideoProjectModal } from "./components/DuplicateVideoProjectModal";
import { VideoProjectPageType, descriptors } from "./descriptors";
import { validateMediasRequiremenst } from "./validations";

import { VideoProjectBubbleDetails } from "./components/BubbleDetails";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { UploadProgressModal } from "@/components/UploadProgressModal";
import { buildPreferenceOptions, StylePreferencesOptions } from "@/utils/projectHelpers";
import { ClientNotesComponent } from "./components/ClientNotesComponent";
import { StylePreferencesComponent } from "./components/StylePreferencesComponent";
import { EditorPlayerInfo } from "./components/EditorPlayerInfo";
import { PageWrapper } from "@/components";
import { ContentPageServices } from "@/services/bedrock";
import { ProjectInfoCard } from "@/containers/Editor/VideoProjectPage/components/ProjectInfoCard";
import { EditorFinalEdit } from "@/containers/Editor/VideoProjectPage/components/EditorFinalEdit";

type Props = RouteComponentProps<{ id: string }>;

const VideoProjectFC: React.FC<Props> = ({ history, match: { params } }) => {
  const { formatMessage } = useIntl();
  const [openArchiveModal, setOpenArchiveModal] = React.useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = React.useState<boolean>(false);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [submittingProject, setSubmittingProject] = React.useState<boolean>(false);
  const [uploadProgressMap, setUploadProgressMap] = React.useState<UploadProgressMap>();
  const [formErrorMessages, setFormErrorMessages] = React.useState<string[] | undefined>(undefined);

  const [videoProject, setVideoProject] = React.useState<VideoProjectDetail | undefined>(undefined);
  const [editable, setEditable] = React.useState<boolean>(false);
  const [videoProjectMessages, setVideoProjectMessages] = React.useState<VideoProjectMessage[] | undefined>(undefined);
  const [needsAck, setNeedsAck] = React.useState<boolean>(false);
  const [playerNotes, setPlayerNotes] = React.useState<ClientNotes | undefined>(videoProject?.clientNotes);
  const [coachNotes, setCoachNotes] = React.useState<ClientNotes | undefined>(videoProject?.coachClientNotes);

  const [videoPreferences, setVideoPreferences] = React.useState<VideoPreferences | undefined>(undefined);
  const [preferencesInfo, setPreferencesInfo] = React.useState<StylePreferencesOptions[]>([]);

  const readyToEdit = videoProject?.status === VideoProjectStatus.SUBMITTED_FOR_EDITING;

  const { data: industryRolesData } = useQuery({
    queryKey: ["industryRoles", videoProject?.coachId, videoProject?.playerId],
    queryFn: () => CoachService.playerIndustryRoles(videoProject!.coachId!, videoProject!.playerId),
    enabled: !!videoProject?.coachId && !!videoProject?.playerId,
  });

  const industryRole = React.useMemo(() => {
    return industryRolesData?.industryRoles[0]?.name || null;
  }, [industryRolesData]);

  // Formik-free state variables
  const [hashtags, setHashtags] = React.useState<string>("");
  const [initialHashtags, setInitialHashtags] = React.useState<string>("");
  const hashtagsInitializedProjectId = React.useRef<number | undefined>(undefined);
  const [showAIModal, setShowAIModal] = React.useState<boolean>(false);
  const [transcript, setTranscript] = React.useState<string | undefined>(undefined);
  const [blockedWordsInCaption, setBlockedWordsInCaption] = React.useState<string>();

  const { hasSetting } = usePlayerSettings(videoProject?.playerId);
  const showHashtagToggle = hasSetting(CoachSettings.PLAYER_HASHTAG, true);

  const { findBlockedWords } = useComplianceChecker({
    playerId: videoProject?.playerId,
    coachId: videoProject?.coachId,
  });

  const { postableSocialNetworks } = useSocialNetworks(true, videoProject?.coachId);

  const validateMessage = (message?: string) => {
    const blockedWords = findBlockedWords(message || "");
    if (blockedWords.length > 0) {
      setBlockedWordsInCaption(blockedWords.join(", "));
    } else {
      setBlockedWordsInCaption(undefined);
    }
  };

  React.useEffect(() => {
    validateMessage(videoProject?.message);
  }, [videoProject?.message, findBlockedWords]);

  React.useEffect(() => {
    const transcripts = videoProject?.post?.mediaGroup?.media
      ?.flatMap(m => m.transcripts?.map(t => t.content))
      ?.sort(t => t?.length || 0)
      ?.reverse();
    if (transcripts && transcripts.length > 0) {
      setTranscript(transcripts[0]);
    } else {
      setTranscript(undefined);
    }
  }, [videoProject?.post?.mediaGroup?.media]);

  const formDisabled = !(
    videoProject?.status === VideoProjectStatus.SUBMITTED_FOR_EDITING ||
    (videoProject?.status !== VideoProjectStatus.POSTED && videoProject?.editable)
  );

  React.useEffect(() => {
    if (videoProject && hashtagsInitializedProjectId.current !== videoProject.videoProjectId) {
      const tagsVal = videoProject.post?.hashtags || "";
      setHashtags(tagsVal);
      setInitialHashtags(tagsVal);
      hashtagsInitializedProjectId.current = videoProject.videoProjectId;
    }
  }, [videoProject]);

  const socialNetworksPosted = videoProject?.post?.socialNetworks || [];
  const [selectedSocialNetwork, setSelectedSocialNetwork] = React.useState<any | undefined>(undefined);

  React.useEffect(() => {
    if (socialNetworksPosted.length > 0 && !selectedSocialNetwork) {
      setSelectedSocialNetwork(socialNetworksPosted[0]?.socialNetworkId);
    }
  }, [socialNetworksPosted]);

  const withCaption = React.useMemo(() => {
    return socialNetworksPosted.some(sn => sn.caption && sn.caption.length > 0);
  }, [socialNetworksPosted]);

  const finalMessage = React.useMemo(() => {
    if (withCaption) {
      const socialPost = socialNetworksPosted.find(sn => sn.socialNetworkId === selectedSocialNetwork);
      return socialPost?.caption || "";
    } else {
      return videoProject?.message || "";
    }
  }, [selectedSocialNetwork, socialNetworksPosted, videoProject?.message, withCaption]);

  const hasCaptionCoach = React.useMemo(() => {
    return hasSetting(CoachSettings.CAPTION_COACH, true);
  }, [hasSetting]);

  const handleUpdateField = (field: keyof VideoProjectDetail, value: any) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleMediaUpdated = (newMedia: Media[]) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        editedMedia: {
          media: newMedia.map((item, sortOrder) => ({
            ...item,
            sortOrder,
          })),
          mediaGroupId: prev.editedMedia?.mediaGroupId || null,
          type: "REELS",
        } as any,
      };
    });
  };

  React.useEffect(() => {
    if (params["id"]) {
      setSelectedSocialNetwork(undefined);
      setLoading(true);
      VideoProjectServices.get(params["id"])
        .then(vp => {
          setVideoProject(vp);
          setPlayerNotes(vp.clientNotes);
          setCoachNotes(vp.coachClientNotes);
          setEditable(!vp.archived && vp.status !== VideoProjectStatus.POSTED && !!vp.post);
          setVideoProjectMessages(vp.messages);
          setNeedsAck(validatePendingAck(vp.ackList, "EDITOR"));
          setVideoPreferences(vp.preferences);
        })
        .catch(() => {
          alert("Error getting video project info");
          history.goBack();
        })
        .finally(() => {
          setLoading(false);
        });
      VideoProjectServices.acknowledgeMessage(Number(params["id"])).catch(e => {
        console.error("acknowledgeMessage error", e);
      });
    }
  }, [params["id"]]);

  const getErrors = (): string[] | undefined => {
    const errors: string[] = [];
    const validMedias = validateMediasRequiremenst(videoProject?.editedMedia);

    if (!validMedias) {
      errors.push(
        formatMessage({
          ...descriptors[VideoProjectPageType.wrongSpecificationsMessage],
        })
      );
    }

    if (
      videoProject?.editedMedia !== undefined &&
      videoProject?.editedMedia.media !== undefined &&
      videoProject?.editedMedia.media.length > 0
    ) {
      videoProject?.editedMedia?.media.forEach(media => {
        if (!media.validAspectRatio && media.validAspectRatio !== undefined) {
          errors.push(
            formatMessage(
              {
                ...descriptors[VideoProjectPageType.wrongAspectRatioMessage],
              },
              { type: media.type.split("/")[0], typeAfterDot: capitalizeFormatter(media.type.split("/")[0]) }
            )
          );
        }

        if (media.errors?.wrongBitrate) {
          errors.push(
            formatMessage({
              ...descriptors[VideoProjectPageType.wrongBitrateMessage],
            })
          );
        }
        if (media.errors?.wrongLength) {
          errors.push(
            formatMessage({
              ...descriptors[VideoProjectPageType.wrongDurationoMessage],
            })
          );
        }
        if (media.errors?.wrongSize) {
          errors.push(
            formatMessage(
              {
                ...descriptors[VideoProjectPageType.wrongSizeMessage],
              },
              { type: media.type.split("/")[0], typeAfterDot: capitalizeFormatter(media.type.split("/")[0]) }
            )
          );
        }
      });
    }

    return errors.length > 0 ? errors : undefined;
  };

  React.useEffect(() => {
    setFormErrorMessages(() => getErrors());
  }, [videoProject]);

  const updateVideoProject = async (): Promise<any> => {
    if (!videoProject) return;

    const errors: string[] = [];
    if (!videoProject.message || !videoProject.message.trim()) {
      errors.push("Caption is required");
    }

    if (!videoProject.editedMedia?.media || videoProject.editedMedia.media.length === 0) {
      errors.push("Final Edit is required");
    }

    if (errors.length > 0) {
      setFormErrorMessages(errors);
      return;
    }

    setFormErrorMessages(undefined);

    const { editedMedia, playerId, coachId, preferredPostDate } = videoProject;
    if (!editedMedia) {
      return;
    }
    const updated: any = videoProject;

    const socialNetworkMessages = videoProject.socialNetworks
      ? videoProject.socialNetworks.map((key: string) => {
          const sn = {
            code: key,
          };
          return sn;
        })
      : [];

    const uploader = new ProjectFileUploader("videoProject", playerId);
    const uploadMap: UploadProgressMap = {};
    editedMedia.media.forEach((mediaObj: Media) => {
      uploadMap[mediaObj.id] = { progress: 0, task: undefined };
    });
    setUploadProgressMap(uploadMap);
    setUploadingFiles(true);
    editedMedia.media = await uploader.prepareMedia(editedMedia.media, (fileId, progress) => {
      setUploadProgressMap(map => {
        return { ...map, [fileId]: { progress, task: undefined } };
      });
    });
    setUploadingFiles(false);
    setSubmittingProject(true);

    const request: VideoProjectUpdateRequest = {
      playerId,
      coachId,
      socialNetworkMessages,
      hashtags: hashtags ? hashtags : undefined,
      messageToPost: videoProject.message,
      status: videoProject.status,
      isAutoPost: videoProject.isAutoPost,
      preferredPostDate,
      editedMediaGroup: editedMedia,
      allowShortLink: videoProject.allowShortLink,
      allowComplianceDisclaimer: videoProject.allowComplianceDisclaimer,
      allowPlayerHashtags: videoProject.allowPlayerHashtags,
    };

    const videoProjectId = videoProject.videoProjectId!!;
    const contentPagesPromise = ContentPageServices.getContentPages({ videoProjectId, perPage: 100 });
    const updateVideoProjectPromise = VideoProjectServices.update(request, videoProjectId);

    Promise.all([contentPagesPromise, updateVideoProjectPromise])
      .then(async ([contentPages, _]) => {
        if (contentPages.content.length > 0) {
          const originalMedia = editedMedia.media.map(media => ({ ...media, id: -1 })) as Media[];
          const updateContentPagePromises = contentPages.content.map(contentPage => {
            return ContentPageServices.updateContentPage(contentPage.id, { originalMedia });
          });
          await Promise.all(updateContentPagePromises);
        }
        toast.success("Project submitted successfully!");
        history.replace(URLS.editor.editQueueList);
      })
      .catch(e => {
        setFormErrorMessages([e.substring(0, 100)]);
      })
      .finally(() => {
        setSubmittingProject(false);
        setLoading(false);
      });
  };

  const getPageActions = (): PageActionProps[] => {
    if (!videoProject) return [];

    const isLoading = uploadingFiles || loading || submittingProject;
    const actions: PageActionProps[] = [];

    if (editable) {
      const saveButtonTitle = readyToEdit ? "Submit Edit" : "Re-submit";
      actions.push({ text: saveButtonTitle, onClick: updateVideoProject, loading: isLoading });
    }

    actions.push({ text: "Duplicate", icon: "copy", onClick: () => setOpenDuplicateModal(true), disabled: isLoading });

    if (!videoProject.archived) {
      actions.push({ text: "Archive", icon: "archive", onClick: () => setOpenArchiveModal(true), disabled: isLoading });
    }

    return actions;
  };

  const archiveModal = (): JSX.Element => {
    let title = formatMessage({ ...descriptors[VideoProjectPageType.archiveAlertTitle] });
    let okHandler = () => {
      VideoProjectServices.archive(videoProject!.videoProjectId!!)
        .then(() => {
          history.push(URLS.editor.editQueueList);
        })
        .catch((e: any) => {
          alert(e);
        });
    };

    return (
      <ConfirmationModal
        openConfirmationModal={openArchiveModal}
        title={title}
        onClose={() => setOpenArchiveModal(false)}
        rejectHandler={() => setOpenArchiveModal(false)}
        okHandler={okHandler}
      />
    );
  };

  const duplicateModal = (): JSX.Element | undefined => {
    if (!videoProject || !videoProject.videoProjectId) return undefined;

    const onDuplicateHandler = (videoProject: VideoProject) => {
      setOpenDuplicateModal(false);
      history.push(URLS.editor.editVideo.replace(":id", `${videoProject.videoProjectId}`));
    };

    return (
      <DuplicateVideoProjectModal
        open={openDuplicateModal}
        videoProjectId={videoProject.videoProjectId}
        isVideoProjectArchived={!!videoProject.archived}
        onClose={() => setOpenDuplicateModal(false)}
        onDuplicate={onDuplicateHandler}
      />
    );
  };

  const refreshMessages = (): void => {
    VideoProjectServices.get(`${videoProject?.videoProjectId!!}`).then((r: VideoProjectDetail) => {
      setVideoProjectMessages(r.messages);
    });
  };

  React.useEffect(() => {
    setPreferencesInfo(buildPreferenceOptions(videoPreferences));
  }, [videoPreferences]);

  return (
    <PageWrapper
      title={`Video Project #${videoProject?.videoProjectId}`}
      actions={getPageActions()}
      loading={loading}
      breadcrumbs={[{ name: "Editing Queue", link: URLS.editor.editQueueList }]}
    >
      {archiveModal()}
      {duplicateModal()}

      <Dialog open={submittingProject} onOpenChange={undefined} size="sm" disableEscapeDismissal={true}>
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-sm font-semibold select-none">
            <Loader2 className="animate-spin size-4 text-indigo-600 shrink-0" />
            <span>Submitting Project</span>
          </Dialog.Title>
        </Dialog.Header>

        <Dialog.Content className="p-5 flex flex-col items-center justify-center gap-4 text-center">
          <div className="text-xs text-slate-600 font-medium">
            Please wait while we submit your video project changes. This may take a few moments.
          </div>
        </Dialog.Content>
      </Dialog>

      {videoProject && (
        <div className="flex flex-col gap-6">
          {formErrorMessages && formErrorMessages.length > 0 && (
            <div className="flex flex-col gap-6 w-full text-left">
              <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl text-rose-600 text-sm flex flex-col gap-1 select-none">
                {formErrorMessages.map((err: string, i: number) => (
                  <div key={i} className="font-semibold">
                    • {err}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 flex flex-col gap-6">
              <EditorPlayerInfo
                playerId={videoProject.playerId}
                industryRole={industryRole}
                videoProjectId={videoProject.videoProjectId!!}
                coachId={videoProject.coachId}
              />

              {/* Project Files section */}
              <EditorProjectFiles videoProject={videoProject} />

              <EditorFinalEdit videoProject={videoProject} handleMediaUpdated={handleMediaUpdated} />

              {/* Post Info */}
              <EditorPostInfo
                videoProject={videoProject}
                disabled={formDisabled}
                hasCaptionCoach={!!hasCaptionCoach}
                showHashtagToggle={!!showHashtagToggle}
                postableSocialNetworks={postableSocialNetworks}
                finalMessage={finalMessage}
                hashtags={hashtags}
                initialHashtags={initialHashtags}
                setHashtags={setHashtags}
                onChangeField={handleUpdateField}
                setShowAIModal={setShowAIModal}
                blockedWordsInCaption={blockedWordsInCaption}
              />
            </div>
            <div className="lg:col-span-2 flex flex-col gap-6">
              <ProjectInfoCard videoProject={videoProject} setVideoProject={setVideoProject} />
              <ClientNotesComponent
                playerId={videoProject?.playerId}
                coachId={videoProject?.coachId}
                playerNotes={playerNotes}
                coachNotes={coachNotes}
                onUpdate={(pNotes, cNotes) => {
                  setPlayerNotes(pNotes);
                  setCoachNotes(cNotes);
                }}
              />
              <StylePreferencesComponent preferencesInfo={preferencesInfo} formatHexColor={formatHexColor} />

              {videoProjectMessages && (
                <VideoProjectBubbleDetails
                  onSuccess={refreshMessages}
                  videoProjectId={videoProject?.videoProjectId!!}
                  submitBtnLabel={"Add Message"}
                  messages={videoProjectMessages}
                  needsAck={needsAck}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <UploadProgressModal open={uploadingFiles} progressMap={uploadProgressMap} />

      {showAIModal && (
        <CaptionCoachDialog
          open={showAIModal}
          onOpenChange={setShowAIModal}
          coachIdOverride={videoProject?.coachId}
          playerIdOverride={videoProject?.playerId}
          industryRoleOverride={industryRole || undefined}
          context={transcript}
          contextType="video transcript"
          handleAcceptedContent={({ caption: newCaption, hashtags: addedHashtags }) => {
            setShowAIModal(false);
            handleUpdateField("message", newCaption);

            if (addedHashtags && addedHashtags.length > 0) {
              const newTags: string[] = [];
              if (hashtags.length > 0) {
                hashtags.split(" ").forEach(tag => newTags.push(tag));
              }

              addedHashtags.forEach(tag => {
                if (newTags.indexOf(tag) === -1) {
                  newTags.push(tag);
                }
              });
              setHashtags(newTags.join(" "));
            }
          }}
        />
      )}
    </PageWrapper>
  );
};

export const EditorVideoProjectPage = withRouter(VideoProjectFC);
